import {
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { KpiFilterAndTargetConfiguration } from 'controllers/Analytics/SalesLeadTimeController'
import moment from 'moment'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  configuration: KpiFilterAndTargetConfiguration
  saveConfiguration: (data: KpiFilterAndTargetConfiguration) => void
  saveTarget: (value: number, unit: string) => void
  targetValueLabel: string
}

const SelectTargetDate = (props: {
  targetValueLabel: string
  configuration: KpiFilterAndTargetConfiguration
  handleChange: (
    configuration: Partial<KpiFilterAndTargetConfiguration>
  ) => void
}) => {
  return (
    <TextField
      type="number"
      label={props.targetValueLabel}
      className="target-respose-time"
      margin="dense"
      InputLabelProps={{
        shrink: true,
        style: {
          whiteSpace: 'nowrap',
        },
      }}
      // fullWidth
      inputProps={
        props.configuration.unit === '%'
          ? {
              min: '0',
              max: '100',
              step: '0.05',
            }
          : {
              min: '1',
              max: '360',
              step: '1',
            }
      }
      style={{
        marginRight: '4px',
        textAlign: 'center',
      }}
      onChange={(e) => {
        let targetLeadTimeValue = parseFloat(e.target.value)

        if (props.configuration.unit === '%') {
          targetLeadTimeValue = targetLeadTimeValue / 100
        }

        if (targetLeadTimeValue > 360) return

        props.handleChange({
          value: targetLeadTimeValue,
        })
      }}
      value={
        props.configuration.unit === '%'
          ? (100 * props.configuration.value).toFixed(0)
          : props.configuration.value
      }
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {props.configuration.unit === '%' ? (
              <span>%</span>
            ) : (
              <Select
                margin="dense"
                value={props.configuration.unit || 'days'}
                onChange={(e) => {
                  props.handleChange({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    unit: e.target.value as any,
                  })
                }}
              >
                <MenuItem value="days">days</MenuItem>
                <MenuItem value="hours">hours</MenuItem>
                <MenuItem value="minutes">minutes</MenuItem>
                <MenuItem value="seconds">seconds</MenuItem>
              </Select>
            )}
          </InputAdornment>
        ),
      }}
    />
  )
}

export function KpiFiltersAndTargetConfiguration(props: Props) {
  const [configuration, setConfiguration] = React.useState(props.configuration)
  const [startDate, setStartDate] = React.useState<Date>(undefined)
  const [endDate, setEndDate] = React.useState<Date>(undefined)
  const shouldSaveConfiguration = useRef(false)
  const shouldSaveTarget = useRef(false)
  const { t, i18n } = useTranslation('common')
  const [reload, shouldReload] = React.useState(false)

  i18n.on('languageChanged', () => {
    shouldReload(true)
    setTimeout(() => shouldReload(false), 1000)
  })

  useEffect(() => {
    switch (configuration.startDate) {
      case 'month':
        setStartDate(moment().subtract(1, 'month').toDate())
        break
      case 'quarter':
        setStartDate(moment().subtract(4, 'months').toDate())
        break
      default:
        setStartDate(new Date(configuration.startDate))
    }

    switch (configuration.endDate) {
      case 'today':
        setEndDate(new Date())
        break
      default:
        setEndDate(new Date(configuration.endDate))
    }
  }, [configuration])

  const handleChange = (values: Partial<KpiFilterAndTargetConfiguration>) => {
    shouldSaveConfiguration.current = true
    setConfiguration((config) => ({
      ...config,
      ...values,
    }))
  }

  const handleChangeTarget = (
    values: Partial<KpiFilterAndTargetConfiguration>
  ) => {
    shouldSaveTarget.current = true
    setConfiguration((config) => ({
      ...config,
      ...values,
    }))
  }

  return (
    <div className="graph-configuration">
      <LoadingContainer loading={reload} noMessage>
        <KeyboardDatePicker
          autoOk
          disableToolbar
          disableFuture
          variant="inline"
          format={moment.defaultFormat}
          label={t('common:start-date', 'start date')}
          margin="dense"
          value={startDate}
          onChange={(moment) => {
            if (!moment) return

            handleChange({
              startDate: moment.toISOString(),
            })
          }}
          labelFunc={(day) => {
            return t('common:formattedDate', { date: day.toDate() })
          }}
        />
        <KeyboardDatePicker
          autoOk
          disableToolbar
          disableFuture
          variant="inline"
          format={moment.defaultFormat}
          label={t('common:end-date', 'end date')}
          margin="dense"
          value={endDate}
          labelFunc={(day) => {
            return t('common:formattedDate', { date: day.toDate() })
          }}
          onChange={(moment) => {
            if (!moment) return
            handleChange({
              endDate: moment.toISOString(),
            })
          }}
        />
        <Typography variant="caption">
          <SelectTargetDate
            targetValueLabel={props.targetValueLabel}
            configuration={configuration}
            handleChange={handleChangeTarget}
          />
        </Typography>

        {/* <QuantityFieldMUI
        decimalScale={0}
        value={{
          selectableAbbreviations: ['seconds', 'minutes', 'hours', 'days'],
          value: configuration.targetLeadTimeValue || 2,
          unit: configuration.targetLeadTimeUnit || 'days',
        }}
        onChange={(quantity) => {
          handleChange({
            targetLeadTimeUnit: quantity.unit as any,
            targetLeadTimeValue: quantity.value,
          })
        }}
      /> */}
        <Button
          variant="text"
          className="btn-save"
          color="primary"
          onClick={() => {
            if (shouldSaveConfiguration.current)
              props.saveConfiguration(configuration)
            if (shouldSaveTarget.current) {
              props.saveTarget(configuration.value, configuration.unit)
            }
            shouldSaveConfiguration.current = false
            shouldSaveConfiguration.current = false
          }}
        >
          save
        </Button>
      </LoadingContainer>
    </div>
  )
}
